import React, { useState } from "react";
import PropTypes from "prop-types";
import { criteriaToStatement } from "../sourceCriteria";
import SkuBreakdownModal from "./SkuBreakdownModal";
import CleanupSection from "./CleanupSection";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

// Tiny inline "in-flight" indicator — matches the codebase idiom used in BaseWidget,
// JobStatusModal, etc. so the spinner reads as "system is recalculating" everywhere.
const Spinner = ({ size = 3 }) => (
  <span
    aria-label="recalculating"
    role="status"
    className={`animate-spin rounded-full border-b-2 border-text-2 inline-block`}
    style={{ width: `${size * 4}px`, height: `${size * 4}px` }}
  />
);
Spinner.propTypes = { size: PropTypes.number };

const SourceReview = ({ criteria, preview, typeName, sourceTypeId, excludeSourceId, valueLabels, loading, cleanupPlan, cleanupSelected, onToggleCleanup }) => {
  // SKU-breakdown drill-down — opens a modal aggregating the matched cost by (service, SKU)
  // so finance can audit the *composition* of the spend (the question they're really asking
  // when they open this view). Modal fetches lazily so we don't waste a BQ query per keystroke.
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // Never show the source being edited as overlapping itself. The backend already excludes it;
  // this is a defensive guard so a stale/edge response can't surface a self-overlap.
  const overlaps = (preview?.overlaps || []).filter((o) => excludeSourceId == null || o.sourceId !== excludeSourceId);
  const byValue = preview?.byValue || [];
  // Map raw facet value -> friendly name. Seed with picker-derived labels so the statement
  // shows the project's friendly name even before the preview round-trip lands (or when the
  // preview matched zero cost). Preview-resolved labels then win on conflict.
  const labels = { ...(valueLabels || {}) };
  byValue.forEach((b) => { if (b.label) labels[b.value] = b.label; });
  // A project source with no values picked claims the type across every project — current
  // and future. That breadth is easy to miss, so flag it and frame the breakdown as the
  // projects matched *today*, not the source's scope. AI-workspace sources are a different
  // axis (OpenAI cost, no project_id), so the "every project" breadth never applies to them.
  const aiScope = (criteria?.scope?.ai_workspaces || []).length > 0;
  // True when no projects are scoped on the project axis — the source spans every project.
  const allProjects = !aiScope && (criteria?.scope?.projects || []).length === 0;
  // Visual rules for the in-flight state (financial honesty):
  //   - The statement is computed locally, so it stays at full opacity (it's never stale).
  //   - Every block whose value depends on the backend preview dims to ~60% opacity while
  //     in flight, so a stale number is never silently displayed as authoritative.
  //   - A thin pulsing bar at the top of the cost card signals "system is recalculating"
  //     for peripheral vision; an inline spinner sits next to the headline cost so a
  //     direct read of the number can never miss the staleness signal.
  const staleClass = loading ? "opacity-60 transition-opacity" : "transition-opacity";

  // Suppress the "Includes" panel when the only sample-service IS the source type itself —
  // that panel is meant to surface sub-services for a *category-level* type (e.g. "Managed
  // Data" → Cloud SQL + Spanner). When the type is already a leaf (e.g. type = "Cloud SQL",
  // services = [{name: "Cloud SQL"}]), the row just duplicates the picker.
  const sampleServices = preview?.sampleServices || [];
  const showIncludes =
    sampleServices.length > 1 ||
    (sampleServices.length === 1 && sampleServices[0]?.name !== typeName);
  // Hide the drill-down button until the preview has landed AND matched something. There's
  // nothing meaningful to sample at €0/no-result.
  const canDrillDown = (preview?.totalMonthlyCost || 0) > 0 && !loading;

  // Trust-signal row that sits right under the statement — "No overlap ✓" is the answer to
  // "am I about to double-count?" and finance reads it second only to the matched cost.
  // When overlaps DO exist we keep them in the dedicated section below, but still surface
  // a one-line warning chip here so the user can't miss it scrolling.
  const hasOverlap = overlaps.length > 0;
  // Split overlaps by rank (backend `ranksAhead`): sources that out-rank this one claim the
  // shared cost first — they're already excluded from the residual above ("claimed ahead of
  // you"). The rest also match, but this source out-ranks them, so it claims that cost.
  const claimsAhead = overlaps.filter((o) => o.ranksAhead);
  const alsoMatches = overlaps.filter((o) => !o.ranksAhead);

  // Residual the source actually captures vs. the inclusive project total (residual + cost
  // claimed by higher-priority sources). Showing "of €X in scope" stops the smaller residual
  // from looking like missing data. youOutrankCost is the part of the residual currently on
  // LOWER-priority sources that will move here on save — flag it so the headline figure is
  // honest about where it comes from.
  const residual = preview?.totalMonthlyCost || 0;
  const projectTotal = preview?.projectTotalMonthlyCost || 0;
  const claimedAheadCost = claimsAhead.reduce((s, o) => s + (o.monthlyCost || 0), 0);
  const youOutrankCost = alsoMatches.reduce((s, o) => s + (o.monthlyCost || 0), 0);
  const hasProjectContext = projectTotal > residual + 0.5; // some cost is claimed ahead

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-text-2 mb-1">This source captures</p>
        <div className="rounded-card bg-surface-2 px-4 py-3 text-sm text-text">{criteriaToStatement(criteria, typeName, labels)}</div>
        {/* Inline status chips: visible trust signals directly under the statement. */}
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {allProjects && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[11px] text-warning">
              <span aria-hidden>⚠</span> Includes projects added later
            </span>
          )}
          {!hasOverlap && (
            <span className={`inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[11px] text-success ${staleClass}`}>
              <span aria-hidden>✓</span> No overlap
            </span>
          )}
          {hasOverlap && (
            <span className={`inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[11px] text-warning ${staleClass}`}>
              <span aria-hidden>⚠</span> {overlaps.length} overlapping source{overlaps.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-card border border-card-bd overflow-hidden">
        {/* Top progress bar — visible only while in flight. Reads as "system busy" without
            interrupting the value's read flow. */}
        <div className="h-0.5 bg-surface-2">
          {loading && <div className="h-full bg-text-3 animate-pulse" aria-hidden />}
        </div>
        <div className="px-4 py-3">
          <div className={`flex justify-between items-baseline mb-2 ${staleClass}`}>
            <span className="text-xs text-text-2">Cost captured (last 30d)</span>
            <span className="flex items-center gap-2">
              {loading && <Spinner />}
              <span className="text-base font-semibold text-text">{euro(preview?.totalMonthlyCost)}<span className="text-xs font-normal text-text-3">/mo</span></span>
            </span>
          </div>
          {hasProjectContext && (
            <p className={`text-[11px] text-text-2 mb-1 ${staleClass}`}>
              of {euro(projectTotal)}/mo in scope · <span className="text-warning">{euro(claimedAheadCost)} claimed ahead</span>
            </p>
          )}
          {youOutrankCost > 0.5 && (
            <p className={`text-[11px] text-success mb-1 ${staleClass}`}>
              Includes {euro(youOutrankCost)}/mo now on lower-priority sources — it moves here when saved.
            </p>
          )}
          {allProjects && byValue.length > 0 && (
            <p className={`text-[11px] text-text-3 mb-1 ${staleClass}`}>Currently matches {byValue.length} project{byValue.length === 1 ? "" : "s"}</p>
          )}
          <div className={staleClass}>
            {byValue.map((b) => (
              <div key={b.value} className="flex justify-between text-xs text-text-2 py-0.5">
                <span className="truncate">{b.label || b.value}</span>
                <span>{euro(b.monthlyCost)}</span>
              </div>
            ))}
          </div>
          {/* Audit drill-down — opens the SKU breakdown so finance can verify the spend
              composition. The button is only offered when there's matched cost to audit
              and the preview isn't in flight (no stale audit affordance). */}
          {canDrillDown && (
            <button
              type="button"
              onClick={() => setBreakdownOpen(true)}
              className="mt-2 text-[11px] text-primary hover:text-primary-hover underline underline-offset-2"
            >
              Audit by SKU →
            </button>
          )}
        </div>
      </div>

      {showIncludes && (
        <div className={staleClass}>
          <span className="text-xs font-medium uppercase tracking-wide text-text-2">Includes</span>
          {/* Cost-bearing breakdown rather than bare chips — for category-level types
              ("Managed Data") finance wants to see which sub-services drive the headline. */}
          <div className="mt-1 space-y-0.5">
            {sampleServices.map((s) => (
              <div key={s.name} className="flex justify-between text-xs text-text-2 py-0.5">
                <span className="truncate">{s.name}</span>
                <span className="tabular-nums">{euro(s.monthlyCost)}<span className="text-[10px] font-normal text-text-3">/mo</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {cleanupPlan && onToggleCleanup ? (
        <CleanupSection plan={cleanupPlan} selected={cleanupSelected || []} onToggle={onToggleCleanup} />
      ) : null}

      {/* Detailed overlap list — only renders when there ARE overlaps. The clean-state
          "No overlap ✓" is already surfaced as a chip directly under the statement. Split
          into two buckets so the user can tell apart cost lost to higher-priority sources
          (claimed ahead) from cost this source claims off lower-priority ones (you out-rank). */}
      {hasOverlap && (
        <div className={`text-sm space-y-3 ${staleClass}`}>
          {claimsAhead.length > 0 && (
            <div>
              <span className="text-xs font-medium uppercase tracking-wide text-text-2">Claimed ahead of you</span>
              <ul className="mt-1 space-y-1">
                {claimsAhead.map((o) => (
                  <li key={o.sourceId} className="text-warning flex justify-between">
                    <span>⚠ {o.displayName || `source ${o.sourceId}`}</span>
                    <span>{euro(o.monthlyCost)} taken first</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-text-3">These out-rank this source, so their cost is already excluded from the captured total above.</p>
            </div>
          )}
          {alsoMatches.length > 0 && (
            <div>
              <span className="text-xs font-medium uppercase tracking-wide text-text-2">You out-rank these</span>
              <ul className="mt-1 space-y-1">
                {alsoMatches.map((o) => (
                  <li key={o.sourceId} className="text-success flex justify-between">
                    <span>{o.displayName || `source ${o.sourceId}`}</span>
                    <span>{euro(o.monthlyCost)} you claim</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-text-3">This source claims this cost ahead of them; lower its priority if they should win instead.</p>
            </div>
          )}
        </div>
      )}

      <SkuBreakdownModal
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        criteria={criteria}
        sourceTypeId={sourceTypeId}
        totalMatched={preview?.totalMonthlyCost}
        valueLabels={labels}
      />
    </div>
  );
};

SourceReview.propTypes = {
  criteria: PropTypes.object.isRequired,
  preview: PropTypes.object,
  typeName: PropTypes.string,
  // Source type id, threaded through to the SampleRowsModal so its sample-rows query
  // matches the preview's matched-cost predicate exactly.
  sourceTypeId: PropTypes.string,
  excludeSourceId: PropTypes.number,
  // Optional fallback labels (raw value -> friendly name) sourced from the value picker.
  // Used when the preview hasn't landed yet, or when the predicate returned zero matches.
  valueLabels: PropTypes.object,
  // True while a preview request is debounced or in flight. Every region whose value
  // depends on the backend response dims to ~60% opacity, the headline cost gains an
  // inline spinner, and a thin pulsing bar shows at the top of the cost card.
  loading: PropTypes.bool,
  cleanupPlan: PropTypes.shape({ covered: PropTypes.array }),
  cleanupSelected: PropTypes.arrayOf(PropTypes.number),
  onToggleCleanup: PropTypes.func,
};

export default SourceReview;
